/**
 * PropertyDetail Page - Minimalistic Redesign
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 2.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Complete minimalistic redesign with clean slate tones, consistent with Home page design.
 * Booking form preserved as primary CTA with orange brand-sun styling.
 * 
 * Design System:
 * - Eyebrow text: ui-eyebrow-brand
 * - Headlines: text-brand-ocean font-black tracking-tight
 * - CTAs: bg-brand-sun uppercase tracking-[0.15em] font-black rounded-2xl
 * - Stats: bg-slate-50 border border-slate-100 rounded-2xl
 * - Clean minimal sections with consistent spacing
 * 
 * Changelog:
 * - 2.0.0 (2025-03-09): Complete minimalistic redesign
 */

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ProIcon } from '../components/ProIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { ImageGalleryModal } from '../components/ImageGalleryModal';
import { BookingIframeModal } from '../components/BookingIframeModal';
import { ShareButton } from '../components/ShareButton';
import { CalendarModal } from '../components/CalendarModal';
import { PropertyMap } from '../components/PropertyMap';
import { StickyBookBar } from '../components/StickyBookBar';
import { useSearchBar } from '../contexts/SearchBarContext';
import {
  trackBeginCheckout,
  trackEvent,
  trackGenerateLead,
  trackShare,
  trackViewItem,
} from '../lib/analytics';
import { isFavorite, toggleFavorite } from '../lib/favorites';

// Types
interface Review {
  id: string;
  author: string;
  avatar: string;
  date: string;
  rating: number;
  content: string;
}

interface PropertyData {
  id: string;
  title: string;
  location: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  guests: number;
  squareMeters: number;
  poolType: 'private' | 'community';
  gardenType: 'private' | 'community';
  description: string;
  amenities: string[];
  images: string[];
  badges: string[];
  lat: number;
  lng: number;
  host: {
    name: string;
    avatar: string;
    since: string;
    reviews: number;
    rating: number;
    isSuperhost: boolean;
  };
  reviews: Review[];
  rating: number;
  reviewCount: number;
}

// Mock data - In real app, fetch from API
// This data matches the MOCK_PROPERTIES[0] from mock/properties.ts (Villa Luna del Mar)
const mockProperty: PropertyData = {
  id: '1001',
  title: 'Villa Luna del Mar',
  location: 'Marbella, Costa del Sol',
  price: 450,
  bedrooms: 4,
  bathrooms: 3,
  guests: 8,
  squareMeters: 250,
  poolType: 'private',
  gardenType: 'private',
  description: 'Villa Luna del Mar is designed for relaxed, sun-filled holidays right by the coast. Wake up to sea views, spend afternoons by the private pool, and enjoy outdoor dinners in a sheltered garden made for long evenings. The layout works beautifully for families, with spacious bedrooms, practical shared areas, and quick beach access for easy days out. Restaurants, beach clubs, and essentials are all close enough to keep your stay smooth and stress-free.',
  amenities: [
    'Heated Pool', 'Gated Garden', 'BBQ Area', 'High-speed WiFi', 
    'Air Conditioning', 'Baby Cot', 'High Chair', 'Private Parking',
    'Fully Equipped Kitchen', 'Smart TV', 'Outdoor Dining', 'Sun Loungers'
  ],
  images: [
    'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&h=800&fit=crop',
    'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop',
  ],
  badges: ['Premium', 'Beach Access', 'Heated Pool'],
  lat: 36.4985,
  lng: -4.8088,
  host: {
    name: 'SUNMARBELLA',
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face',
    since: '2013',
    reviews: 127,
    rating: 4.95,
    isSuperhost: true,
  },
  rating: 4.8,
  reviewCount: 127,
  reviews: [
    {
      id: '1',
      author: 'Sarah M.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
      date: 'December 2025',
      rating: 5,
      content: 'Perfect family vacation! The pool was heated which was great for the kids. The rental had everything we needed and the location was ideal - walking distance to the beach.',
    },
    {
      id: '2',
      author: 'James & Emma',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
      date: 'November 2025',
      rating: 5,
      content: 'SUNMARBELLA made everything so easy. High chair and cot were set up when we arrived. The rental is even better than the photos!',
    },
    {
      id: '3',
      author: 'The Johnson Family',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      date: 'October 2025',
      rating: 5,
      content: 'Our third time staying with SUNMARBELLA. Consistently excellent. The kids love the pool and we love the peaceful location.',
    },
  ],
};

export const PropertyDetail = () => {
  const { id = '1' } = useParams();
  const navigate = useNavigate();
  const { setCurrentProperty, searchParams, updateSearchParams } = useSearchBar();
  
  // Touch state for mobile image carousel swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [mobileImageIndex, setMobileImageIndex] = useState(0);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && mobileImageIndex < property.images.length - 1) {
      setMobileImageIndex(prev => prev + 1);
    }
    if (isRightSwipe && mobileImageIndex > 0) {
      setMobileImageIndex(prev => prev - 1);
    }
  };

  const reviewsRef = useRef<HTMLDivElement>(null);
  const mobileReviewsRef = useRef<HTMLDivElement>(null);
  const bookingCardRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  // Slide reviews function for mobile
  const slideReviews = (direction: 'left' | 'right') => {
    const container = mobileReviewsRef.current;
    if (!container) return;
    
    const card = container.querySelector<HTMLElement>('[data-review-card]');
    if (!card) return;
    
    const scrollAmount = card.offsetWidth + 16; // card width + gap
    const currentScroll = container.querySelector('.overflow-x-auto')?.scrollLeft || 0;
    
    const newScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;
      
    container.querySelector('.overflow-x-auto')?.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };

  // Scroll to section function
  const scrollToSection = (section: 'reviews' | 'location') => {
    const ref = section === 'reviews' ? reviewsRef : locationRef;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // State
  const [property] = useState<PropertyData>(mockProperty);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [showMobileStickyBar, setShowMobileStickyBar] = useState(true); // Always show on mobile
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showDesktopFooterBar, setShowDesktopFooterBar] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  
  // Use dates from search context
  const [startDate, endDate] = searchParams.dateRange;
  const { adults, children, infants } = searchParams.guests;
  const totalGuests = adults + children + infants;

  // ICNEA booking URL construction
  const icneaCompanyId = (import.meta as any).env?.VITE_ICNEA_COMPANY_ID as string | undefined;
  
  const bookingUrl = useMemo(() => {
    const query = new URLSearchParams({
      emp: icneaCompanyId || '0000',
      hot: id.padStart(4, '0'),
      idi: '2',
      ent: startDate ? formatDateForIcnea(startDate) : '',
      sor: endDate ? formatDateForIcnea(endDate) : '',
      per: totalGuests.toString(),
      sup: '1',
    });
    return `https://tpv.icnea.net/reserves.aspx?${query.toString()}`;
  }, [icneaCompanyId, id, totalGuests, startDate, endDate]);

  // IntersectionObserver removed - no sticky booking header needed
  // Header remains default with search bar only per design requirements

  // Scroll detection for mobile sticky bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      
      // Check if booking form is visible in viewport
      const bookingFormRect = bookingCardRef.current?.getBoundingClientRect();
      const isBookingFormVisible = bookingFormRect && 
        bookingFormRect.top < window.innerHeight && 
        bookingFormRect.bottom > 0;
      
      // Show sticky bar when scrolled past hero (400px) AND booking form is NOT visible
      setShowMobileStickyBar(scrollY > 400 && !isBookingFormVisible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver to show footer bar when sidebar is NOT visible
  useEffect(() => {
    if (!bookingCardRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show footer when sidebar is NOT intersecting (not visible)
        const bookingFormRect = bookingCardRef.current?.getBoundingClientRect();
        const isBookingFormVisible = bookingFormRect && 
          bookingFormRect.top < window.innerHeight && 
          bookingFormRect.bottom > 0;
        
        setShowDesktopFooterBar(!entry.isIntersecting && !isBookingFormVisible);
      },
      {
        threshold: 0.1,
        rootMargin: '-100px 0px 0px 0px'
      }
    );

    observer.observe(bookingCardRef.current);
    return () => observer.disconnect();
  }, []);

  // Analytics tracking
  useEffect(() => {
    trackViewItem({
      id,
      name: property.title,
      price: property.price,
      category: 'Rental',
      location: property.location,
    });
  }, [id, property]);

  // Load wishlist state from cookie on mount
  useEffect(() => {
    setIsWishlisted(isFavorite(property.id));
  }, [property.id]);

  // Set current property in context
  useEffect(() => {
    setCurrentProperty({
      id,
      title: property.title,
      price: property.price,
      images: property.images,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      guests: property.guests,
      location: property.location,
    });
    
    return () => setCurrentProperty(null);
  }, [id, property, setCurrentProperty]);

  // Close booking dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      // Don't close if clicking the selector buttons
      if (target.closest('.date-selector-button') || target.closest('.guest-selector-button')) {
        return;
      }
      if (!target.closest('.guest-dropdown-container')) {
        setShowGuestDropdown(false);
      }
    };
    
    if (showGuestDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showGuestDropdown]);

  const handleBookNow = () => {
    trackBeginCheckout({
      id,
      name: property.title,
      price: property.price,
      category: 'Rental',
    });

    if (!startDate || !endDate) {
      trackEvent('booking_requirements_missing', { reason: 'missing_dates', property_id: id });
      setShowCalendar(true);
      return;
    }

    if (!icneaCompanyId) {
      trackEvent('booking_requirements_missing', { reason: 'missing_company_id', property_id: id });
      alert('Set VITE_ICNEA_COMPANY_ID to enable live booking.');
      return;
    }

    setIsBookingModalOpen(true);
  };

  const handleToggleWishlist = () => {
    const newState = toggleFavorite(property.id);
    setIsWishlisted(newState);
  };

  const nights = startDate && endDate 
    ? Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))
    : 0;
  const subtotal = property.price * nights;
  const cleaningFee = 150;
  const total = subtotal + cleaningFee;

  // Icon mapping for amenities
  const iconMap: { [key: string]: string } = {
    'heated': 'fire',
    'pool': 'person-swimming-pool',
    'wifi': 'wifi',
    'high-speed': 'wifi',
    'parking': 'car',
    'private': 'car',
    'air': 'snowflake',
    'kitchen': 'utensils',
    'fully': 'utensils',
    'tv': 'tv',
    'smart': 'tv',
    'baby': 'baby',
    'high': 'baby',
    'garden': 'island-tropical',
    'gated': 'shield-halved',
    'bbq': 'fire',
    'outdoor': 'island-tropical',
    'sun': 'island-tropical',
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back Button removed - Header has search functionality */}

      <main className="pt-0 md:pt-6 pb-36 md:pb-0">
        {/* Title Section - Above Gallery for SEO */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[5rem] pb-4 hidden md:block">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="ui-section-title md:text-4xl mb-2 break-words">
                {property.title}
              </h1>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600">
                <span className="flex items-center gap-1">
                  <ProIcon icon="star" family="solid" className="text-base text-brand-sun" />
                  <span className="font-semibold text-slate-900">{property.rating}</span>
                </span>
                <span className="underline cursor-pointer hover:text-slate-900">
                  ({property.reviewCount} reviews)
                </span>
                <span>·</span>
                <span>{property.location}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <ShareButton url={window.location.href} title={property.title} />
              <button 
                onClick={handleToggleWishlist}
                className={`ui-btn-icon w-10 h-10 rounded-full border-none transition-colors ${
                  isWishlisted ? 'bg-[rgba(245,130,31,0.12)]' : ''
                }`}
                aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-pressed={isWishlisted}
              >
                <ProIcon 
                  icon="heart" 
                  family={isWishlisted ? 'solid' : 'light'}
                  className={`text-xl ${isWishlisted ? 'text-brand-sun' : 'text-slate-600'}`} 
                />
              </button>
            </div>
          </div>
        </section>

        {/* Image Gallery Grid - Reduced Height */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[320px] lg:h-[380px] rounded-2xl overflow-hidden group relative">
            <div 
              className="col-span-2 row-span-2 relative cursor-pointer overflow-hidden"
              onClick={() => { setCurrentImageIndex(0); setIsGalleryOpen(true); }}
            >
              <img 
                src={property.images[0]} 
                alt={property.title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </div>
            {property.images.slice(1, 5).map((img, idx) => (
              <div 
                key={idx}
                className="relative cursor-pointer overflow-hidden"
                onClick={() => { setCurrentImageIndex(idx + 1); setIsGalleryOpen(true); }}
              >
                <img 
                  src={img}
                  alt={`${property.title} - ${idx + 2}`}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>
            ))}
          </div>

          {/* Mobile Image Carousel */}
          <div 
            className="md:hidden relative h-[300px] -mx-4 mt-16 overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={() => { setCurrentImageIndex(mobileImageIndex); setIsGalleryOpen(true); }}
          >
            <img 
              src={property.images[mobileImageIndex]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            {/* Share button overlay - always visible on mobile as icon-only */}
            <div className="absolute top-4 right-4 z-30" onClick={(e) => e.stopPropagation()}>
              <ShareButton url={window.location.href} title={property.title} iconOnly />
            </div>
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur text-slate-900 px-3 py-1.5 rounded-full text-sm font-medium shadow">
              {mobileImageIndex + 1} / {property.images.length} photos
            </div>
            {/* Swipe indicators */}
            {property.images.length > 1 && (
              <div className="absolute bottom-4 left-4 flex gap-1">
                {property.images.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      index === mobileImageIndex ? 'bg-white w-4' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Main Content Grid - FIXED STICKY LAYOUT */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            {/* Left Column - Property Info */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              
              {/* Mobile Title */}
              <div className="md:hidden">
                <p className="ui-eyebrow-brand mb-2">Featured Rental</p>
                <h1 className="ui-title-compact mb-2">
                  {property.title}
                </h1>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <ProIcon icon="star" family="solid" className="text-base text-brand-sun" />
                    <span className="font-semibold text-slate-900">{property.rating}</span>
                  </span>
                  <button 
                    onClick={() => scrollToSection('reviews')}
                    className="underline cursor-pointer hover:text-slate-900 bg-transparent border-none p-0"
                  >
                    ({property.reviewCount} reviews)
                  </button>
                  <span>·</span>
                  <button 
                    onClick={() => scrollToSection('location')}
                    className="hover:text-brand-ocean transition-colors bg-transparent border-none p-0"
                  >
                    {property.location}
                  </button>
                </div>
              </div>

              {/* Stats Row - Clean Minimal */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                {[
                  { icon: 'bed', value: property.bedrooms, label: 'beds' },
                  { icon: 'bath', value: property.bathrooms, label: 'baths' },
                  { icon: 'ruler-combined', value: `${property.squareMeters}m²`, label: 'size' },
                  { icon: 'family', value: property.guests, label: 'guests' },
                  { icon: 'person-swimming-pool', value: property.poolType, label: 'pool' },
                  { icon: 'island-tropical', value: property.gardenType, label: 'garden' },
                ].map((stat, idx) => {
                  // Add safety check for stat icon
                  if (!stat.icon) {
                    console.warn('No icon found for stat:', stat);
                    return null;
                  }
                  return (
                    <div key={idx} className="text-center px-1.5 sm:px-4 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl bg-slate-50 border border-slate-100">
                      <ProIcon icon={stat.icon} family="duotone" className="text-xl text-brand-ocean mb-1.5 sm:mb-2" />
                      <div className="text-base sm:text-xl font-black text-slate-900 leading-none">{stat.value}</div>
                      <div className="ui-stat-label">{stat.label}</div>
                    </div>
                  );
                })}
              </div>

              
              
              {/* Why SUNMARBELLA */}
              <div className="border-t border-slate-100 pt-8">
                <p className="ui-eyebrow-muted mb-3">Why Book With SUNMARBELLA</p>
                <h2 className="ui-title-compact mb-6">
                  Book It Right The First Time.
                </h2>
                <div className="space-y-4">
                  {[
                    { title: 'Personally Inspected', desc: 'Checked in person for the details that matter.' },
                    { title: 'Rates up to 20% lower', desc: 'Better rates, clearer terms, no platform markup.' },
                    { title: 'Local Support', desc: 'Real people in Marbella. Fast help when you need it.' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-brand-ocean/10 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-brand-ocean" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="pt-0.5">
                        <h3 className="ui-feature-title">{item.title}</h3>
                        <p className="ui-feature-copy">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description - Moved above Amenities */}
              <div className="border-t border-slate-100 pt-8">
                <div className="flex items-center gap-2 mb-3">
                  <ProIcon icon="house-building" family="light" className="text-xl text-brand-ocean" />
                  <p className="ui-eyebrow-brand">About</p>
                </div>
                <h3 className="ui-title mb-4">This Rental</h3>
                <p className="ui-feature-copy leading-relaxed">
                  {property.description}
                </p>
              </div>

              {/* Family Features */}
              <div className="bg-slate-50 ui-radius-panel p-6 sm:p-8 border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <ProIcon icon="baby" family="light" className="text-xl text-brand-ocean" />
                  <p className="ui-eyebrow-brand">Family Friendly</p>
                </div>
                <h3 className="ui-title mb-4">Perfect for Families</h3>
                <div className="space-y-4">
                  {[
                    'Travel cot and high chair available on request',
                    'Gated pool area for child safety',
                    'Beach within 5-minute walk',
                    'Quiet residential area, perfect for early bedtimes',
                  ].map((feature, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-brand-ocean/10 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-brand-ocean" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="ui-feature-copy pt-0.5">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - STICKY BOOKING SIDEBAR */}
            <div className="lg:col-span-1 lg:sticky lg:top-20 lg:self-start">
              <div 
                ref={bookingCardRef}
                data-chat-blocker="booking-cta"
                className="bg-white border border-slate-100 ui-radius-panel ui-shadow-panel-ocean p-6"
              >
                {/* Price Header - Redesigned with reviews on upper right */}
                <div className="mb-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Price Info */}
                    <div className="flex-1 min-w-0">
                      {startDate && endDate ? (
                        <div>
                          <div className="ui-eyebrow-muted mb-1">Total price</div>
                          <div className="ui-price-xl truncate">€{total.toLocaleString()}</div>
                          <div className="ui-caption-muted mt-1">{nights} nights • €{property.price}/night</div>
                        </div>
                      ) : (
                        <div>
                          <div className="ui-eyebrow-muted mb-1">Price per night</div>
                          <div className="ui-price-xl truncate">€{property.price.toLocaleString()}</div>
                          <div className="ui-caption-muted mt-1">Select dates for total</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Right: Reviews Badge */}
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1.5 bg-brand-sun/10 border border-brand-sun/20 rounded-xl px-3 py-1.5">
                        <ProIcon icon="star" family="solid" className="text-base text-brand-sun" />
                        <span className="font-bold text-slate-900">{property.rating}</span>
                      </div>
                      <span className="text-xs text-slate-500">{property.reviewCount} reviews</span>
                    </div>
                  </div>
                </div>

                {/* Date & Guest Selector - SearchBar Style */}
                <div className="space-y-2 mb-4">
                  {/* Dates - Opens CalendarModal like SearchBar */}
                  <button 
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowGuestDropdown(false);
                      setShowCalendar(true);
                    }}
                    className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors flex items-center gap-3 date-selector-button"
                  >
                    <div className="w-9 h-9 bg-slate-200/70 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ProIcon icon="calendar-range" family="light" className="text-xl text-slate-600" />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="ui-eyebrow-muted">Dates</div>
                      <div className="ui-field-strong truncate">
                        {startDate && endDate
                          ? `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                          : 'Add Dates'}
                      </div>
                    </div>
                  </button>

                  {/* Guests */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowGuestDropdown(!showGuestDropdown);
                    }}
                    className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors flex items-center gap-3 guest-selector-button"
                  >
                    <div className="w-9 h-9 bg-slate-200/70 rounded-xl flex items-center justify-center flex-shrink-0">
                      <ProIcon icon="family-dress" family="light" className="text-xl text-slate-600" />
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <div className="ui-eyebrow-muted">Guests</div>
                      <div className="ui-field-strong">{totalGuests} {totalGuests === 1 ? 'guest' : 'guests'}</div>
                    </div>
                  </button>
                </div>

                <AnimatePresence>
                  {showGuestDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-white ui-radius-panel ui-shadow-panel p-6 z-50 mb-4 guest-dropdown-container border border-slate-100"
                    >
                      <GuestCounter 
                        label="Adults"
                        sublabel="Ages 13+"
                        value={adults}
                        onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, adults: val } })}
                        min={1}
                      />
                      <GuestCounter 
                        label="Children"
                        sublabel="Ages 2-12"
                        value={children}
                        onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, children: val } })}
                        min={0}
                      />
                      <GuestCounter 
                        label="Infants"
                        sublabel="Under 2"
                        value={infants}
                        onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, infants: val } })}
                        min={0}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* CTA Button - SearchBar Style */}
                <button
                  onClick={handleBookNow}
                  className="ui-btn-primary w-full py-3.5 gap-2"
                >
                  <span>{startDate && endDate ? 'BOOK NOW' : 'GET PRICE'}</span>
                </button>
                
                <p className="text-center ui-caption-muted mt-3">
                  You won't be charged yet
                </p>
                
                {startDate && endDate && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-xl">
                    <p className="text-center ui-caption-strong">
                      40% due now (€{Math.round(total * 0.4).toLocaleString()})
                    </p>
                  </div>
                )}

                {startDate && endDate && (
                  <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
                    <div className="flex justify-between ui-caption-muted">
                      <span>€{property.price} x {nights} nights</span>
                      <span className="font-bold">€{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between ui-caption-muted">
                      <span>Cleaning fee</span>
                      <span className="font-bold">€{cleaningFee}</span>
                    </div>
                    <div className="flex justify-between font-black text-slate-900 pt-3 border-t border-slate-100">
                      <span>Total</span>
                      <span>€{total.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 ui-caption-neutral">
                    <ProIcon icon="house-building" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span className="font-medium">Booked 3 times in the last week</span>
                  </div>
                  <div className="flex items-center gap-2 ui-caption-muted">
                    <ProIcon icon="family-dress" family="light" className="text-base" />
                    <span>8 guests are viewing this property</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Amenities Section - Full Width */}
        <section className="border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-3">
              <ProIcon icon="person-shelter" family="light" className="text-xl text-brand-ocean" />
              <p className="ui-eyebrow-brand">Features</p>
            </div>
            <h2 className="ui-title mb-4">What This Place Offers</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {property.amenities.slice(0, showAllAmenities ? undefined : 8).map((amenity, idx) => {
                const firstWord = amenity.toLowerCase().split(' ')[0];
                const icon = iconMap[firstWord] || 'check';
                return (
                  <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-100">
                    <div className="w-8 h-8 bg-brand-ocean/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ProIcon icon={icon} family="light" className="text-xl text-brand-ocean" />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{amenity}</span>
                  </div>
                );
              })}
            </div>
            
            {property.amenities.length > 8 && (
              <button 
                onClick={() => setShowAllAmenities(!showAllAmenities)}
                className="ui-btn-secondary w-full sm:w-auto mt-6 py-3 text-sm"
              >
                {showAllAmenities ? 'Show Less' : 'SHOW ALL'}
              </button>
            )}
          </div>
        </section>

        {/* Location Section - Full Width */}
        <section ref={locationRef} id="location" className="border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="flex items-center gap-2 mb-3">
              <ProIcon icon="map-location-dot" family="light" className="text-xl text-brand-ocean" />
              <p className="ui-eyebrow-brand">Location</p>
            </div>
            <h2 className="ui-title mb-4">{property.location}</h2>
            
            <div className="bg-slate-100 rounded-2xl overflow-hidden relative ui-map-height" id="property-map">
              <PropertyMap properties={[property]} selectedPropertyId={property.id} />
            </div>
            
            <p className="mt-6 ui-feature-copy">
              Located in prestigious Elviria, Marbella East. Walking distance to beaches, restaurants, and golf courses. 
              This exclusive area offers the perfect blend of tranquility and convenience, with easy access to Marbella's 
              world-class amenities.
            </p>
          </div>
        </section>

        {/* Reviews Section - Full Width */}
        <section ref={reviewsRef} id="reviews" className="border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
            {/* Header with consistent typography */}
            <div className="text-center mb-10 sm:mb-16">
              <div className="flex items-center justify-center gap-2 mb-3">
                <ProIcon icon="star" family="solid" className="text-xl text-brand-ocean" />
                <p className="ui-eyebrow-muted">Guest Feedback</p>
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex items-center gap-1.5 bg-brand-sun/10 border border-brand-sun/20 rounded-xl px-4 py-2">
                  <ProIcon icon="star" family="solid" className="text-xl text-brand-sun" />
                  <span className="text-2xl font-black text-slate-900">{property.rating}</span>
                </div>
                <span className="text-slate-400">·</span>
                <span className="ui-caption-strong">{property.reviewCount} reviews</span>
              </div>
            </div>

            {/* Rating Categories - Stats Grid Pattern (matching About page) */}
            <div className="max-w-4xl mx-auto mb-12 sm:mb-16">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Cleanliness', value: 4.9, icon: 'broom' },
                  { label: 'Check-in', value: 4.8, icon: 'check' },
                  { label: 'Communication', value: 5.0, icon: 'key' },
                  { label: 'Location', value: 4.9, icon: 'map-pin' },
                  { label: 'Value', value: 4.8, icon: 'tag' },
                ].map((cat) => (
                  <div key={cat.label} className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                    <ProIcon icon={cat.icon} family="light" className="text-xl text-brand-ocean mx-auto mb-2" />
                    <div className="text-xl font-black text-slate-900 leading-none mb-1">{cat.value}</div>
                    <div className="text-2xs font-bold text-slate-500 uppercase tracking-wide">{cat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Reviews - Horizontal carousel matching Home page UI */}
            <div className="md:hidden" ref={mobileReviewsRef}>
              <div
                className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 ui-mobile-review-track"
              >
                {property.reviews.map((review, idx) => (
                  <div key={review.id} data-review-card className="w-[calc(100vw-2rem)] shrink-0 snap-start bg-white rounded-2xl sm:rounded-3xl border border-slate-100 p-4 sm:p-5 space-y-3 sm:space-y-4 shadow-sm">
                    {/* Season Badge */}
                    <div className="flex items-center justify-between">
                      <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wider rounded-full">
                        {review.date.includes('2024') ? 'Summer 2024' : review.date.includes('2023') ? 'Summer 2023' : 'Recent'}
                      </span>
                      <span className="text-slate-400 text-sm">{review.date}</span>
                    </div>
                    
                    {/* Rating with Score */}
                    <div className="flex items-center gap-2">
                      <div className="flex gap-0.5 text-brand-sun">
                        {[...Array(5)].map((_, i) => (
                          <ProIcon key={i} icon="star" family="solid" className="text-base" />
                        ))}
                      </div>
                      <span className="text-slate-900 font-bold text-sm">{review.rating.toFixed(1)}</span>
                    </div>
                    
                    <p className="text-slate-700 text-sm sm:text-base leading-relaxed">"{review.content}"</p>
                    
                    <div className="space-y-1">
                      <p className="text-slate-900 text-sm sm:text-base font-semibold break-words">— {review.author}</p>
                      <div className="flex items-center gap-2 text-slate-500 text-sm">
                        <ProIcon icon="star" family="solid" className="text-base text-brand-ocean" />
                        <span>Verified guest</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Mobile Review Navigation - matching Home page */}
              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => slideReviews('left')}
                  className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                  aria-label="Previous review"
                >
                  <ProIcon icon="chevron-left" family="light" className="text-xl text-slate-600" />
                </button>
                <span className="text-sm text-slate-500">
                  Swipe or tap to navigate
                </span>
                <button
                  onClick={() => slideReviews('right')}
                  className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                  aria-label="Next review"
                >
                  <ProIcon icon="chevron-right" family="light" className="text-xl text-slate-600" />
                </button>
              </div>
            </div>

            {/* Desktop Reviews - Grid, show 4 by default */}
            <div className="hidden md:block">
              <div className="grid grid-cols-2 gap-6 mb-10">
                {property.reviews.slice(0, showAllReviews ? undefined : 4).map((review) => (
                  <div key={review.id} className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-4 mb-4">
                      <img 
                        src={review.avatar}
                        alt={review.author}
                        className="w-12 h-12 rounded-full object-cover border-2 border-slate-100"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-bold text-slate-900 text-sm">{review.author}</div>
                            <div className="ui-caption-muted">{review.date}</div>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {[...Array(5)].map((_, i) => (
                              <ProIcon 
                                key={i} 
                                icon="star" 
                                family="solid"
                                className={`text-sm ${i < review.rating ? 'text-brand-sun' : 'text-slate-200'}`} 
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{review.content}</p>
                  </div>
                ))}
              </div>

              {/* Show All Button */}
              {property.reviews.length > 4 && (
                <div className="text-center">
                  <button 
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="ui-btn-secondary px-8 py-3 text-sm"
                  >
                    {showAllReviews ? 'SHOW LESS' : `SHOW ALL ${property.reviews.length} REVIEWS`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

      </main>

      {/* Desktop Sticky Bottom Bar - Using Shared Component */}
      {showDesktopFooterBar && createPortal(
        <StickyBookBar
          mode="property"
          isVisible={showDesktopFooterBar}
          price={property.price}
          rating={property.rating}
          reviewCount={property.reviewCount}
          title={property.title}
          location={property.location}
          totalPrice={startDate && endDate ? total : undefined}
          nights={startDate && endDate ? nights : undefined}
          onBookNow={handleBookNow}
        />,
        document.body
      )}

      {/* Mobile Sticky Bottom Bar - Using Shared Component with Children */}
      {createPortal(
        <AnimatePresence>
          {showMobileStickyBar && (
            <StickyBookBar
              mode="property"
              isVisible={showMobileStickyBar}
              price={property.price}
              rating={property.rating}
              reviewCount={property.reviewCount}
              title={property.title}
              location={property.location}
              totalPrice={startDate && endDate ? total : undefined}
              nights={startDate && endDate ? nights : undefined}
              onBookNow={handleBookNow}
            >
              {!startDate || !endDate ? (
                // No dates selected - show date/guest selectors
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowCalendar(true)}
                      className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                      <ProIcon icon="calendar-range" family="light" className="text-base text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">
                        {startDate && endDate
                          ? `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                          : 'Add dates'}
                      </span>
                    </button>
                    <button
                      onClick={() => setShowGuestDropdown(true)}
                      className="flex-1 flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
                    >
                      <ProIcon icon="family-dress" family="light" className="text-base text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">{totalGuests} guests</span>
                    </button>
                  </div>
                  <button
                    onClick={() => setShowCalendar(true)}
                    className="ui-btn-primary w-full py-3 rounded-xl text-sm"
                  >
                    Get Price
                  </button>
                </div>
              ) : null}
            </StickyBookBar>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile Guest Bottom Sheet - Portaled to escape z-1 main content stacking context */}
      {showGuestDropdown && createPortal(
        <div className="fixed inset-0 z-[9998] md:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowGuestDropdown(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl p-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-5" />
            <h3 className="text-lg font-bold text-slate-900 mb-4">Guests</h3>
            <GuestCounter 
              label="Adults"
              sublabel="Ages 13+"
              value={adults}
              onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, adults: val } })}
              min={1}
            />
            <GuestCounter 
              label="Children"
              sublabel="Ages 2-12"
              value={children}
              onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, children: val } })}
              min={0}
            />
            <GuestCounter 
              label="Infants"
              sublabel="Under 2"
              value={infants}
              onChange={(val) => updateSearchParams({ guests: { ...searchParams.guests, infants: val } })}
              min={0}
            />
            <button
              onClick={() => setShowGuestDropdown(false)}
              className="ui-btn-ocean w-full mt-4 py-3.5"
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Calendar Modal - Used for both mobile and desktop (no flexibility for booking) */}
      <CalendarModal
        isOpen={showCalendar}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setShowCalendar(false)}
        onDatesChange={(dates) => {
          updateSearchParams({ dateRange: dates });
          setShowCalendar(false);
        }}
        showFlexibleOption={false}
      />

      {/* Image Gallery Modal */}
      {isGalleryOpen && (
        <ImageGalleryModal
          images={property.images}
          isOpen={isGalleryOpen}
          currentIndex={currentImageIndex}
          onClose={() => { setIsGalleryOpen(false); }}
          onPrevious={() => setCurrentImageIndex((prev) => Math.max(0, prev - 1))}
          onNext={() => setCurrentImageIndex((prev) => Math.min(property.images.length - 1, prev + 1))}
          onSelectImage={setCurrentImageIndex}
          propertyTitle={property.title}
          propertyLocation={property.location}
          propertyPrice={property.price}
          onBookNow={handleBookNow}
          totalPrice={startDate && endDate ? total : undefined}
          nights={startDate && endDate ? nights : undefined}
          rating={property.rating}
          reviewCount={property.reviewCount}
        />
      )}

      {/* Booking Modal */}
      <BookingIframeModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        bookingUrl={bookingUrl}
        propertyName={property.title}
      />
    </div>
  );
};

// Helper Components - SearchBar Style

function GuestCounter({ 
  label, 
  sublabel, 
  value, 
  onChange, 
  min 
}: { 
  label: string; 
  sublabel: string; 
  value: number; 
  onChange: (val: number) => void;
  min: number;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div>
        <div className="ui-control-text tracking-tight text-slate-900">{label}</div>
        <div className="ui-eyebrow-subtle">{sublabel}</div>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="ui-icon-toggle disabled:opacity-30"
        >
          <ProIcon icon="minus" family="utility" className="text-xs" />
        </button>
        <span className="w-6 text-center ui-value-lg">{value}</span>
        <button
          onClick={() => onChange(value + 1)}
          className="ui-icon-toggle"
        >
          <ProIcon icon="plus" family="utility" className="text-xs" />
        </button>
      </div>
    </div>
  );
}

function formatDateForIcnea(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export default PropertyDetail;
